<template>
    <vx-modal
        :visible="!!model"
        @update:visible="close"
        :padding="false"
        size="md"
    >
        <template v-slot:header>
            <div class="px-6 pt-6">
                Are you sure you want to delete this {{ modelTitle }}?
            </div>
        </template>
        <template v-slot:subheader>
            <div class="px-6">
                This {{ modelTitle }} and it's association to contacts will be deleted. There is no undo.
            </div>
        </template>
        <div class="p-6">
            <div v-if="loadingUsage">
                <vx-icon-loading class="w-9 h-9" :spin="true"></vx-icon-loading>
            </div>
            <vx-alert v-else-if="failedToLoadUsage" color="danger" :closeable="false">
                Failed to load data.
                <a href="#" @click.prevent="loadUsage()">Retry.</a>
            </vx-alert>
            <vx-alert v-else :color="segmentCount ? 'info' : 'primary'" :closeable="false" >
                {{ text }}
            </vx-alert>
        </div>
        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click="close"
                    size="lg"
                    color="muted">
                    Cancel
                </vx-button>
                <vx-button @click="remove"
                    data-test="del-btn"
                    :disabled="loadingUsage"
                    type="button"
                    size="lg"
                    color="danger"
                    class="lg:grow">
                    Delete
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { VxAlert, VxButton, VxIconLoading, VxModal } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import { pluralized } from '~/components/filters';
import { segmentsClient , tagsClient , customAttributesClient } from '~/services';
import { useToasted } from '~/composables/useToasted';

const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const model = defineModel({
    type: String,
});
const emit = defineEmits(['deleted']);

const props = defineProps({
    type: {
        type: String,
        validator: type => ['tag', 'custom_attribute'].includes(type),
    }
});

const loadingUsage = ref(false);
const failedToLoadUsage = ref(false);
const segmentCount = ref(null);
const segmentNames = ref([]);

const fieldName = computed(() => {
    switch (props.type) {
        case 'tag':
            return 'filter[tag_name]';
        case 'custom_attribute':
            return 'filter[custom_attribute_key]';
    }

    return undefined;
});

const modelTitle = computed(() => {
    switch (props.type) {
        case 'tag':
            return 'tag';
        case 'custom_attribute':
            return 'custom attribute';
    }

    return undefined;
});

const text = computed(() => {
    if (segmentCount.value === null) {
        return '';
    }

    if (!segmentCount.value) {
        return `The ${modelTitle.value} is not used in any segments.`;
    }

    let label = `The ${modelTitle.value} is used in ` + pluralized('segment', segmentCount.value, true);

    label += ': ' + segmentNames.value.join(', ');
    label += segmentNames.value.length < segmentCount.value
        ? ', etc.'
        : '.';

    return label;
});

watch(() => model.value, (value) => {
    if (value) {
        loadUsage();
    }
});

const remove = async () => {
    let request;

    if (props.type === 'tag') {
        request = tagsClient.tagDelete(teamId, model.value);
    } else if (props.type === 'custom_attribute') {
        request = customAttributesClient.delete(teamId, model.value);
    } else {
        return;
    }

    request.then(() => {
        emit('deleted', model.value);
        close();
    }).catch((error) => {
        console.error(error);
        toasted.global.platform_error();
    });
};

const close = () => {
    model.value = null;
};

const loadUsage = async () => {
    if (!model.value || !fieldName.value) {
        return;
    }

    loadingUsage.value = true;
    failedToLoadUsage.value = false;

    try {
        const { data } = await segmentsClient.getSegments({ teamId }, {
            [fieldName.value]: model.value,
            per_page: 5,
        });

        const { data: dataCount } = await segmentsClient.getSegmentsCount({ teamId }, {
            [fieldName.value]: model.value,
        })

        segmentCount.value = dataCount.count;
        segmentNames.value = data.data.map((segment) => segment.name);
    } catch {
        failedToLoadUsage.value = true;
    } finally {
        loadingUsage.value = false;
    }
};
</script>
